/**
 * /dental-implant-cost-fort-worth — formerly published implant prices; now
 * a compliant "Why your quote is unique + how to get yours" landing page.
 * Per practice policy, we do NOT show implant dollar amounts anywhere on the
 * public site (every case is unique and pricing only makes sense after a
 * clinical exam). The page keeps its SEO target keywords ("dental implant
 * cost fort worth") but converts them into a free-consultation funnel.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import AppointmentForm from '@/components/forms/AppointmentForm';
import MonthlyPaymentMatcher from '@/components/forms/MonthlyPaymentMatcher';

const COST_FACTORS = [
  {
    icon: '🦷',
    title: 'Number of Implants',
    body: 'Replacing one tooth, several teeth, or a full arch with All-on-4 each have very different treatment plans.',
  },
  {
    icon: '🩻',
    title: 'Bone Health',
    body: "If you've been missing teeth for a while, you may need bone grafting before implants can be placed — this is included in your written quote.",
  },
  {
    icon: '👑',
    title: 'Type of Restoration',
    body: 'Single crowns, bridges, implant-supported dentures, or fixed-arch zirconia each have different lab and material costs.',
  },
  {
    icon: '🛡️',
    title: 'Your Insurance',
    body: 'Many PPO plans cover a portion of implant treatment. We verify your benefits before quoting so you see your real out-of-pocket.',
  },
];

const DentalImplantCost = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Where can I find affordable dental implants near me in Fort Worth?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Fort Worth Dental makes implants affordable for patients across Fort Worth and nearby communities like Benbrook, Lake Worth, Aledo, and Arlington through 0% interest financing (CareCredit, LendingClub), in-house payment plans, HSA/FSA, and most PPO insurance. Rather than a one-size-fits-all price, you get a personalized written quote at a FREE consultation so you know your real out-of-pocket. Call (817) 985-3337."
        }
      },
      {
        "@type": "Question",
        "name": "How much do dental implants cost in Fort Worth?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Dental implant cost depends on the number of implants, bone health, and the type of restoration — every case is unique. Rather than publish prices that won't apply to your situation, Fort Worth Dental gives you a precise written quote at your FREE consultation (a $475 value). Call (817) 985-3337 to schedule."
        }
      },
      {
        "@type": "Question",
        "name": "Does insurance cover dental implants?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Many PPO dental insurance plans cover a portion of dental implant costs. Fort Worth Dental accepts most major plans and verifies your benefits in advance so you know your real out-of-pocket before treatment begins."
        }
      },
      {
        "@type": "Question",
        "name": "What is included in my implant treatment quote?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Your free consultation includes a digital scan, X-rays, and a written treatment plan with the full quote — implant posts, abutments, crowns, and any bone grafting if needed. No hidden fees, no surprises."
        }
      },
      {
        "@type": "Question",
        "name": "Do you offer financing for dental implants?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Fort Worth Dental offers 0% interest financing through CareCredit and LendingClub, in-house payment plans, and accepts HSA/FSA. Most patients qualify for low monthly payments."
        }
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Dental Implant Cost Fort Worth TX | Affordable Implants Near You | Fort Worth Dental</title>
        <meta name="description" content="Looking for affordable dental implants near you in Fort Worth, TX? Every case is unique — get a personalized written quote plus 0% financing and insurance options at your FREE consultation (a $475 value). Call (817) 985-3337." />
        <meta name="keywords" content="affordable dental implants near me, affordable dental implants cost near you, dental implant cost fort worth, how much do dental implants cost fort worth, dental implant financing fort worth, dental implants near me, dental implant pricing texas" />
        <link rel="canonical" href="https://fortworthdental.com/dental-implant-cost-fort-worth" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-br from-teal-600 to-teal-800 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <p className="text-teal-200 font-semibold mb-2">Personalized Pricing • No Surprises</p>
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">Dental Implant Cost in Fort Worth, TX</h1>
            <p className="text-xl mb-6">
              Every smile is unique — so is every implant treatment plan. Get a precise written quote tailored to your situation at your FREE consultation.
            </p>
            <div className="bg-white/10 p-6 rounded-lg mb-8">
              <div className="text-2xl font-bold text-yellow-300">FREE Implant Consultation</div>
              <p className="text-teal-100">Includes 3D scan, X-rays, and a written treatment plan — a $475 value</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="tel:8179853337" className="bg-white text-teal-700 px-8 py-4 rounded-lg font-bold text-center hover:bg-gray-100">
                Get My Personalized Quote
              </a>
              <Link to="/implant-center" className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-center hover:bg-white/10">
                Visit Implant Center
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Monthly Payment Matcher */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4 max-w-2xl">
          <MonthlyPaymentMatcher />
        </div>
      </section>

      {/* Affordable / Near You */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
            Affordable Dental Implants Near You
          </h2>
          <p className="text-lg text-gray-700 mb-6 leading-relaxed">
            "Affordable" doesn't mean a cut-rate implant — it means a treatment plan that fits
            your budget. Patients across Fort Worth and nearby communities like Benbrook, Lake
            Worth, Aledo, White Settlement, and Arlington choose Fort Worth Dental because we
            make tooth replacement attainable without compromising on quality or the credentials
            of who places your implant.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            The most affordable implant is the one that's done right the first time by a
            board-certified specialist — so you never pay twice. At your free consultation we map
            out the exact treatment you need (nothing you don't), verify your insurance, and walk
            you through 0% financing so your investment becomes a comfortable monthly payment.
          </p>
        </div>
      </section>

      {/* Why we don't publish prices */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
            Why We Don't Publish Implant Prices Online
          </h2>
          <p className="text-lg text-gray-700 mb-6 leading-relaxed">
            Honestly? Posting a single dollar amount for "a dental implant" would be misleading. Two patients can walk into our office with the same missing tooth and need completely different treatment — one may need bone grafting, the other may be a candidate for same-day immediate placement. Their final costs aren't the same, and neither should their quote.
          </p>
          <p className="text-lg text-gray-700 mb-10 leading-relaxed">
            We respect your time and your wallet. Instead of generic ranges, we give you a precise, itemized written quote after a quick 30-minute consultation — and that consultation is free.
          </p>

          <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
            What Determines Your Implant Cost
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {COST_FACTORS.map((f) => (
              <div key={f.title} className="bg-gray-50 p-6 rounded-lg flex gap-4">
                <div className="text-3xl flex-shrink-0" aria-hidden="true">{f.icon}</div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">{f.title}</h4>
                  <p className="text-gray-600 text-sm">{f.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose us */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">Why Choose Fort Worth Dental for Implants?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-white p-6 rounded-lg shadow text-center">
              <div className="text-4xl mb-4" aria-hidden="true">🏆</div>
              <h3 className="font-bold text-gray-800 mb-2">Board-Certified Expert</h3>
              <p className="text-gray-600">Dr. Mund is a Diplomate of the American Board of Oral Implantology — the highest credential in implant dentistry.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow text-center">
              <div className="text-4xl mb-4" aria-hidden="true">📋</div>
              <h3 className="font-bold text-gray-800 mb-2">Honest Written Quote</h3>
              <p className="text-gray-600">No hidden fees, no surprise add-ons. Your treatment plan and price are written down before anything begins.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow text-center">
              <div className="text-4xl mb-4" aria-hidden="true">💳</div>
              <h3 className="font-bold text-gray-800 mb-2">0% Financing Available</h3>
              <p className="text-gray-600">CareCredit & LendingClub financing with low monthly payments and 0% interest options. Most patients qualify.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Financing */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-blue-600 to-blue-700 text-white p-8 rounded-lg">
            <h2 className="text-3xl font-bold mb-4">Affordable Payment Options</h2>
            <p className="mb-4 text-blue-50">
              Don't let cost prevent you from getting the smile you deserve. We offer multiple financing options to fit your budget — your treatment coordinator will walk you through every option at your consultation.
            </p>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-blue-50">
              <li className="flex items-center gap-2"><span aria-hidden="true">✓</span> CareCredit — 0% interest options</li>
              <li className="flex items-center gap-2"><span aria-hidden="true">✓</span> LendingClub Patient Solutions</li>
              <li className="flex items-center gap-2"><span aria-hidden="true">✓</span> In-house payment plans</li>
              <li className="flex items-center gap-2"><span aria-hidden="true">✓</span> Most PPO insurance accepted</li>
              <li className="flex items-center gap-2"><span aria-hidden="true">✓</span> HSA / FSA accepted</li>
              <li className="flex items-center gap-2"><span aria-hidden="true">✓</span> $199/year in-house dental plan</li>
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">Implant Cost — Frequently Asked Questions</h2>
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="font-bold text-gray-800 mb-2">Where can I find affordable dental implants near me in Fort Worth?</h3>
              <p className="text-gray-600">Fort Worth Dental makes implants affordable for patients across Fort Worth and nearby communities like Benbrook, Lake Worth, Aledo, and Arlington through 0% interest financing (CareCredit, LendingClub), in-house payment plans, HSA/FSA, and most PPO insurance. Rather than a one-size-fits-all price, you get a personalized written quote at a FREE consultation so you know your real out-of-pocket. Call (817) 985-3337.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="font-bold text-gray-800 mb-2">How much do dental implants cost in Fort Worth?</h3>
              <p className="text-gray-600">Dental implant cost depends on the number of implants, bone health, and the type of restoration — every case is unique. Rather than publish prices that won't apply to your situation, we give you a precise written quote at your FREE consultation. Call (817) 985-3337 to schedule.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="font-bold text-gray-800 mb-2">Does insurance cover dental implants?</h3>
              <p className="text-gray-600">Many PPO dental insurance plans cover a portion of implant treatment. Fort Worth Dental accepts most major plans and verifies your benefits in advance so you know your real out-of-pocket before treatment begins.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="font-bold text-gray-800 mb-2">What's included in my implant treatment quote?</h3>
              <p className="text-gray-600">Your free consultation includes a digital scan, X-rays, and a written treatment plan with the full quote — implant posts, abutments, crowns, and any bone grafting if needed. No hidden fees.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="font-bold text-gray-800 mb-2">Do you offer financing for dental implants?</h3>
              <p className="text-gray-600">Yes. We offer 0% interest financing through CareCredit and LendingClub, in-house payment plans, and accept HSA/FSA. Most patients qualify for low monthly payments.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-teal-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Get Your FREE Dental Implant Consultation</h2>
          <p className="text-xl mb-8">Includes 3D scan, X-rays, and personalized written treatment plan — a $475 value!</p>
          <a href="tel:8179853337" className="bg-white text-teal-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 inline-block">
            Call (817) 985-3337
          </a>
        </div>
      </section>

      {/* Form */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-2xl">
          <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">Request Your FREE Implant Consultation</h2>
          <AppointmentForm source="implant_cost_page" />
        </div>
      </section>
    </div>
  );
};

export default DentalImplantCost;
